// src/pages/reports/IncidentAccidentRegistry.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Modal,
  Row,
  Col,
  Button,
  Table,
  Spinner,
} from "react-bootstrap";
import Select from "../../components/ReportSelect";
import axios from "axios";
import Pagination from "../../components/Pagination";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import "../../styles/Reports.css";

const API = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 20;

// -----------------------------------------------------
// FIXED ACCIDENT/INCIDENT TYPES (from your screenshot)
// -----------------------------------------------------
const FIXED_ACCIDENT_TYPES = [
  "Accident",
  "Claim",
  "Collision",
  "Hazmat Incident",
  "Non-Preventable",
  "Other",
  "Preventable",
  "Rear-ended",
  "Under Review",
].map((name, idx) => ({ value: name, label: name }));

export default function IncidentAccidentRegistry() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // filters (UI pending)
  const [pStart, setPStart] = useState("");
  const [pEnd, setPEnd] = useState("");
  const [pType, setPType] = useState(null);
  const [pDot, setPDot] = useState("both");
  const [pStatus, setPStatus] = useState("both");

  // results
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);

  useEffect(() => {
    const today = new Date();

    // Tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    // 2 months ago
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(today.getMonth() - 2);

    // Format YYYY-MM-DD
    const format = (d) => d.toISOString().split("T")[0];

    setPStart(format(twoMonthsAgo));
    setPEnd(format(tomorrow));
  }, []);

  // ------------------- Helper -----------------------

  const truncateText = (text, maxLength = 80) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0]; // YYYY-MM-DD
  };

  const formatDateTime = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  };

  const renderTrailers = (trailers) => {
    if (!trailers) return "N/A";

    let list = trailers;

    // If stored as a string, parse it
    if (typeof trailers === "string") {
      try {
        list = JSON.parse(trailers);
      } catch {
        return trailers;
      }
    }

    if (!Array.isArray(list) || list.length === 0) return "N/A";

    return (
      <div>
        {list.map((t, idx) => (
          <div key={idx} style={{ marginBottom: "4px" }}>
            {t.type || "—"} - {t.number || "—"}
          </div>
        ))}
      </div>
    );
  };

  const renderNotes = (notes) => {
    if (!notes) return "No Notes";

    let list = notes;

    // Parse string JSON
    if (typeof notes === "string") {
      try {
        list = JSON.parse(notes);
      } catch {
        return notes;
      }
    }

    if (!Array.isArray(list) || list.length === 0) return "No Notes";

    return (
      <div>
        {list.map((n, idx) => (
          <div
            key={idx}
            style={{
              border: "1px solid #e1e1e1",
              borderRadius: "6px",
              padding: "10px",
              marginBottom: "10px",
              background: "#fafafa",
            }}
          >
            <strong>{n.title}</strong>
            <p>{n.body}</p>
          </div>
        ))}
      </div>
    );
  };

  // ------------------- FETCH DATA -----------------------
  const fetchData = async () => {
    setLoading(true);
    setHasSearched(true);
    setCurrentPage(1);

    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`${API}/api/reports/incidents`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: pStart || undefined,
          endDate: pEnd || undefined,
          incType: pType?.value || undefined,
          dot: pDot === "both" ? undefined : pDot,
          status: pStatus === "both" ? undefined : pStatus,
        },
      });

      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Incident Registry Fetch Failed", err);
      setData([]);
    }

    setLoading(false);
  };

  const clearFilters = () => {
    setPStart("");
    setPEnd("");
    setPType(null);
    setPDot("both");
    setPStatus("both");

    setData([]);
    setHasSearched(false);
  };

  // --------------------- CSV EXPORT -----------------------
  const exportCSV = () => {
    if (data.length === 0) {
      alert("No data to export");
      return;
    }

    const header = [
      "Accident No",
      "Accident Type",
      "Truck Number",
      "Driver Name",
      "Accident Date",
      "Province",
      "Country",
      "Description",
      "DOT Reportable",
      "Last Updated",
    ];

    const rows = data.map((r) =>
      [
        r.inc_number,
        r.inc_type || "",
        r.inc_truck_number || "",
        r.driver_name || "",
        r.inc_date || "",
        r.province_name || "",
        r.country_name || "",
        r.inc_description || "",
        r.inc_dot_reportable || "",
        r.inc_saved_date || "",
      ]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );

    const blob = new Blob([[header.join(","), ...rows].join("\n")], {
      type: "text/csv;charset=utf-8",
    });

    saveAs(blob, "IncidentAccidentRegistry.csv");
  };

  // --------------------- VIEW INCIDENT DETAILS MODAL -----------------------
  const openIncident = async (id) => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`${API}/api/reports/incidents/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSelectedIncident(res.data);
      setShowModal(true);
    } catch (err) {
      console.error("Error loading incident:", err);
    }
  };

  // --------------------- PAGINATION -----------------------
  const paginated = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Container className="form-wrapper my-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="fw-bold m-0">📘 Incident / Accident Registry</h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      {/* ---------------- FILTER AREA (MATCHES TIME & ATTENDANCE) ---------------- */}
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={2}>
          <label>Start Date</label>
          <input
            type="date"
            className="form-control"
            value={pStart}
            onChange={(e) => setPStart(e.target.value)}
          />
        </Col>

        <Col md={2}>
          <label>End Date</label>
          <input
            type="date"
            className="form-control"
            value={pEnd}
            onChange={(e) => setPEnd(e.target.value)}
          />
        </Col>

        {/* Accident Type + DOT Reportable (Aligned Row) */}
        <Col md={3}>
          <label>Incident/Accident Types</label>
          <Select
            value={pType}
            onChange={setPType}
            options={FIXED_ACCIDENT_TYPES}
            isClearable
            placeholder="Select..."
          />
        </Col>

        <Col md={3} className="d-flex flex-column justify-content-start">
          <label className="mb-2">DOT Reportable</label>

          <div className="d-flex align-items-center gap-4">
            <label className="d-flex align-items-center">
              <input
                type="radio"
                name="dotFilter"
                value="Yes"
                checked={pDot === "Yes"}
                onChange={(e) => setPDot(e.target.value)}
                style={{ transform: "scale(1.3)", marginRight: "6px" }}
              />
              Yes
            </label>

            <label className="d-flex align-items-center">
              <input
                type="radio"
                name="dotFilter"
                value="No"
                checked={pDot === "No"}
                onChange={(e) => setPDot(e.target.value)}
                style={{ transform: "scale(1.3)", marginRight: "6px" }}
              />
              No
            </label>

            <label className="d-flex align-items-center">
              <input
                type="radio"
                name="dotFilter"
                value="both"
                checked={pDot === "both"}
                onChange={(e) => setPDot(e.target.value)}
                style={{ transform: "scale(1.3)", marginRight: "6px" }}
              />
              Both
            </label>
          </div>
        </Col>
      </Row>

      {/* Buttons Row — EXACT LIKE TIME & ATTENDANCE */}
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md="auto">
          <Button variant="primary" onClick={fetchData}>
            🔍 Filter
          </Button>
          <Button variant="secondary" className="ms-2" onClick={clearFilters}>
            Clear Filter
          </Button>
        </Col>

        <Col md="auto" className="ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || data.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {/* TABLE */}
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : !hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong>.
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm" className="compact-table">
            <thead className="table-dark text-center">
              <tr>
                <th>Accident No</th>
                <th>Accident Type</th>
                <th>Truck Number</th>
                <th>Driver Name</th>
                <th>Accident Date</th>
                <th>Province</th>
                <th>Country</th>
                <th>Description</th>
                <th>DOT Reportable</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-muted">
                    No results found
                  </td>
                </tr>
              ) : (
                paginated.map((i) => (
                  <tr
                    key={i.inc_id}
                    onClick={() => openIncident(i.inc_id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{i.inc_number}</td>
                    <td>{i.inc_type}</td>
                    <td>{i.inc_truck_number}</td>
                    <td>{i.driver_name}</td>
                    <td>{formatDate(i.inc_date)}</td>
                    <td>{i.province_name}</td>
                    <td>{i.country_name}</td>
                    <td title={i.inc_description}>
                      {truncateText(i.inc_description, 70)}
                    </td>
                    <td>{i.inc_dot_reportable}</td>
                    <td>{formatDateTime(i.inc_saved_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Pagination
            currentPage={currentPage}
            totalItems={data.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Incident Detail Modal */}
      {selectedIncident && (
        <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              Incident/Accident Details — #{selectedIncident.inc_number} •{" "}
              {selectedIncident.driver_name}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
            <h5 className="mb-3">Incident / Accident Information</h5>

            <Row className="mb-4">
              <Col md={4}>
                <strong>Incident Type</strong>
                <br />
                {selectedIncident.inc_type}
              </Col>
              <Col md={4}>
                <strong>Truck Number</strong>
                <br />
                {selectedIncident.inc_truck_number}
              </Col>
              <Col md={4}>
                <strong>Police Number</strong>
                <br />
                {selectedIncident.inc_police_number}
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={4}>
                <strong>Call In Time</strong>
                <br />
                {selectedIncident.inc_call_in_time}
              </Col>
              <Col md={4}>
                <strong>Accident Date</strong>
                <br />
                {formatDate(selectedIncident.inc_date)}
              </Col>
              <Col md={4}>
                <strong>Trailer No.</strong>
                <br />
                {renderTrailers(selectedIncident.inc_trailers)}
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={4}>
                <strong>Country</strong>
                <br />
                {selectedIncident.inc_location_country}
              </Col>
              <Col md={4}>
                <strong>Province</strong>
                <br />
                {selectedIncident.inc_location_province}
              </Col>
              <Col md={4}>
                <strong>Timezone</strong>
                <br />
                {selectedIncident.inc_timezone}
              </Col>
            </Row>

            <hr />

            <h5 className="mb-3">Notes</h5>
            <pre style={{ background: "#f9f9f9", padding: "10px" }}>
              {renderNotes(selectedIncident.inc_notes)}
            </pre>

            <hr />

            <h5 className="mb-3">Incident / Accident Questions</h5>

            <Row className="mb-4">
              <Col md={4}>
                <strong>Vehicle Towed</strong>
                <br />
                {selectedIncident.inc_vehicle_towed}
              </Col>
              <Col md={4}>
                <strong>Commercial Vehicle</strong>
                <br />
                {selectedIncident.inc_driver_commercial}
              </Col>
              <Col md={4}>
                <strong>Highway</strong>
                <br />
                {selectedIncident.inc_accident_highway}
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={4}>
                <strong>Needs Insurance</strong>
                <br />
                {selectedIncident.inc_needs_insurance}
              </Col>
              <Col md={4}>
                <strong>Cargo Damaged</strong>
                <br />
                {selectedIncident.inc_cargo_damaged}
              </Col>
              <Col md={4}>
                <strong>Under Dispatch</strong>
                <br />
                {selectedIncident.inc_under_dispatch}
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={4}>
                <strong>Injuries</strong>
                <br />
                {selectedIncident.inc_injuries}
              </Col>
              <Col md={4}>
                <strong>Fatalities</strong>
                <br />
                {selectedIncident.inc_fatalities}
              </Col>
              <Col md={4}>
                <strong>Ticket Issued</strong>
                <br />
                {selectedIncident.inc_ticket_issued}
              </Col>
            </Row>

            <hr />

            <h5 className="mb-3">Damage / Chargebacks</h5>

            <Row className="mb-4">
              <Col md={4}>
                <strong>Truck Damage</strong>
                <br />${selectedIncident.inc_truck_damage}
              </Col>
              <Col md={4}>
                <strong>Trailer Damage</strong>
                <br />${selectedIncident.inc_trailer_damage}
              </Col>
              <Col md={4}>
                <strong>Container Damage</strong>
                <br />${selectedIncident.inc_container_damage}
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={4}>
                <strong>Truck Chargeback</strong>
                <br />${selectedIncident.inc_chargeback_truck}
              </Col>
              <Col md={4}>
                <strong>Trailer Chargeback</strong>
                <br />${selectedIncident.inc_chargeback_trailer}
              </Col>
              <Col md={4}>
                <strong>Container Chargeback</strong>
                <br />${selectedIncident.inc_chargeback_container}
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
}
